import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ChevronDown, Globe, History, Cloud } from 'lucide-react';
import { getLlmModels, LlmModel, getRecentModels } from '@/lib/api/app';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

interface ModelSelectorProps {
  value: string | null;
  onChange: (modelId: string | null) => void;
  disabled?: boolean;
  allowNone?: boolean;
  label?: string;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  allowNone = false,
  label = 'Model',
}) => {
  const [models, setModels] = useState<LlmModel[]>([]);
  const [recentModelIds, setRecentModelIds] = useState<string[]>([]);
  const [isModelsLoading, setIsModelsLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [manualModelInput, setManualModelInput] = useState('');
  const manualInputRef = useRef<HTMLInputElement>(null);

  const fetchModelsAndRecent = async () => {
    setIsModelsLoading(true);
    try {
      const [{ models: fetchedModels }, { models: recent }] = await Promise.all([
        getLlmModels(),
        getRecentModels(),
      ]);
      setModels(fetchedModels);
      setRecentModelIds(recent);
    } catch (e) {
      console.error('Failed to fetch LLM models', e);
    } finally {
      setIsModelsLoading(false);
    }
  };

  useEffect(() => {
    fetchModelsAndRecent();
  }, []);

  const refreshRecentModels = async () => {
    try {
      const { models: recent } = await getRecentModels();
      setRecentModelIds(recent);
    } catch (e) {
      console.error('Failed to refresh recent models', e);
    }
  };

  const handleModelSelect = (modelId: string) => {
    onChange(modelId);
    setPopoverOpen(false);
    refreshRecentModels();
  };

  const handleNoneSelect = () => {
    onChange(null);
    setPopoverOpen(false);
  };

  const handleManualModelSubmit = () => {
    const trimmed = manualModelInput.trim();
    if (trimmed) {
      onChange(trimmed);
      setManualModelInput('');
      setPopoverOpen(false);
      refreshRecentModels();
    }
  };

  const openRouterModels = useMemo(() => models.filter(m => m.provider === 'openrouter'), [models]);
  const zaiModels = useMemo(() => models.filter(m => m.provider === 'zai'), [models]);
  const alibabaModels = useMemo(() => models.filter(m => m.provider === 'alibaba'), [models]);
  const ollamaModels = useMemo(() => models.filter(m => m.provider === 'ollama'), [models]);
  const openaiModels = useMemo(() => models.filter(m => m.provider === 'openai'), [models]);

  const recentModels = useMemo(() => {
    return recentModelIds.map(id => {
      const found = models.find(m => m.id === id);
      return found || { id, name: id, provider: 'custom' as const };
    });
  }, [recentModelIds, models]);

  const selectedModel = useMemo(() => {
    return models.find(m => m.id === value);
  }, [models, value]);

  const displayValue = selectedModel
    ? selectedModel.name
    : (value || (allowNone ? 'None (Default)' : 'Select model'));

  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={popoverOpen}
            className="w-full justify-between text-xs h-8"
            disabled={disabled || isModelsLoading}
          >
            {displayValue}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search models..." className="h-8" />
            <CommandList>
              <CommandEmpty>No models found.</CommandEmpty>
              {allowNone && (
                <CommandGroup heading="Default">
                  <CommandItem
                    value="none-default"
                    onSelect={handleNoneSelect}
                    className="text-xs"
                  >
                    None (Default)
                  </CommandItem>
                </CommandGroup>
              )}
              {recentModels.length > 0 && (
                <CommandGroup heading={
                  <span className="flex items-center gap-2 text-xs">
                    <History className="h-3 w-3" /> Recently Used
                  </span>
                }>
                  {recentModels.map(model => (
                    <CommandItem
                      key={`recent-${model.id}`}
                      value={model.name}
                      onSelect={() => handleModelSelect(model.id)}
                      className="text-xs"
                    >
                      {model.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {ollamaModels.length > 0 && (
                <CommandGroup heading={
                  <span className="flex items-center gap-2 text-xs">
                    <Cloud className="h-3 w-3" /> Ollama Cloud
                  </span>
                }>
                  {ollamaModels.map(model => (
                    <CommandItem
                      key={model.id}
                      value={model.name}
                      onSelect={() => handleModelSelect(model.id)}
                      className="text-xs"
                    >
                      {model.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {zaiModels.length > 0 && (
                <CommandGroup heading={
                  <span className="flex items-center gap-2 text-xs">
                    <Globe className="h-3 w-3" /> Z.AI
                  </span>
                }>
                  {zaiModels.map(model => (
                    <CommandItem
                      key={model.id}
                      value={model.name}
                      onSelect={() => handleModelSelect(model.id)}
                      className="text-xs"
                    >
                      {model.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {alibabaModels.length > 0 && (
                <CommandGroup heading={
                  <span className="flex items-center gap-2 text-xs">
                    <Globe className="h-3 w-3" /> Alibaba
                  </span>
                }>
                  {alibabaModels.map(model => (
                    <CommandItem
                      key={model.id}
                      value={model.name}
                      onSelect={() => handleModelSelect(model.id)}
                      className="text-xs"
                    >
                      {model.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {openRouterModels.length > 0 && (
                <CommandGroup heading={
                  <span className="flex items-center gap-2 text-xs">
                    <Globe className="h-3 w-3" /> OpenRouter
                  </span>
                }>
                  {openRouterModels.map(model => (
                    <CommandItem
                      key={model.id}
                      value={model.name}
                      onSelect={() => handleModelSelect(model.id)}
                      className="text-xs"
                    >
                      {model.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {openaiModels.length > 0 && (
                <CommandGroup heading={
                  <span className="flex items-center gap-2 text-xs">
                    <Globe className="h-3 w-3" /> OpenAI Compatible
                  </span>
                }>
                  {openaiModels.map(model => (
                    <CommandItem
                      key={model.id}
                      value={model.name}
                      onSelect={() => handleModelSelect(model.id)}
                      className="text-xs"
                    >
                      {model.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {/* Manual Model ID Input */}
              <CommandGroup heading={
                <span className="flex items-center gap-2 text-xs">
                  <Cloud className="h-3 w-3" /> Manual Model ID
                </span>
              }>
                <div className="flex items-center gap-1 px-2">
                  <input
                    ref={manualInputRef}
                    type="text"
                    placeholder="Enter model ID (e.g. openai/gpt-4o)"
                    value={manualModelInput}
                    onChange={(e) => setManualModelInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleManualModelSubmit();
                      }
                    }}
                    className="flex-1 h-7 px-2 text-xs bg-transparent border border-input rounded focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={handleManualModelSubmit}
                    disabled={!manualModelInput.trim()}
                  >
                    Set
                  </Button>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ModelSelector;